import React, { useState, useMemo, useEffect } from 'react';
import './Agenda.css';
import { useCache } from '../../hooks/useCache';
import { API_ENDPOINTS, getAuthHeaders } from '../../config/api';
import { cacheGet, cacheSet, emitCacheUpdated } from '../../utils/cache';
import { useSchoolSchedules, getSchoolForDay } from '../../hooks/useSchoolSchedules';

// Force redéploiement - Système cache Activity + Agenda formations complet

// Helpers de dates
const startOfWeek = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Lundi = 0
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfWeek = (d) => {
  const start = startOfWeek(d);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const startOfMonth = (d) => {
  const date = new Date(d);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfMonth = (d) => {
  const date = new Date(d);
  date.setMonth(date.getMonth() + 1);
  date.setDate(0);
  date.setHours(23, 59, 59, 999);
  return date;
};

const ymd = (d) => {
  const date = new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const DAY_NAMES_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTH_NAMES_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Formateur d'heures en timezone Europe/Paris
const fmtParis = new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit', 
  minute: '2-digit', 
  hour12: false, 
  timeZone: 'Europe/Paris'
});

const formatHHMMParis = (isoUtc) => {
  if (!isoUtc) return '';
  try {
    return fmtParis.format(new Date(isoUtc));
  } catch {
    return '';
  }
};

// Helper pour voir les erreurs backend clairement
async function fetchJsonOrThrow(res) {
  if (res.ok) return res.json();
  const text = await res.text().catch(() => '');
  throw new Error(`HTTP ${res.status} – ${text || 'no body'}`);
}

function Agenda({ onNavigate }) {
  const uid = useMemo(() => {
    try {
      return Number(localStorage.getItem('userId') || 0);
    } catch {
      return 0;
    }
  }, []);

  const [view, setView] = useState('week'); // 'week' | 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedOperators, setSelectedOperators] = useState([]); // [] = tous
  const [showOpenDaysOnly, setShowOpenDaysOnly] = useState(false);
  const [toast, setToast] = useState(null);
  const [loadingOverrides, setLoadingOverrides] = useState(false); // 🔥 Animation de chargement
  
  // État du popup d'édition
  const [editPopup, setEditPopup] = useState(null); // { opId, date, shift }

  // Cache pour les opérateurs, weeklyHours et overrides
  const { list: opsList } = useCache({ list: 'ops/list' });
  const { list: weeklyHours } = useCache({ list: 'ops/weeklyHours' });
  const { list: overridesCache } = useCache({ list: 'ops/overrides' });
  
  // État simplifié - une seule source de vérité
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Le cache est maintenant la seule source de vérité
  
  // 🔥 VÉRIFICATION SIMPLE: Étendre le cache si on navigue en dehors
  useEffect(() => {
    const checkAndExtendCache = async () => {
      if (!startOfWeek || !endOfWeek) return;
      
      const meta = cacheGet('ops/overrides:meta');
      if (!meta) return;
      
      const currentFrom = startOfWeek.toISOString().slice(0, 10);
      const currentTo = endOfWeek.toISOString().slice(0, 10);
      
      // Vérifier si on est en dehors de la plage cachée
      const needsExtension = currentFrom < meta.from || currentTo > meta.to;
      
      if (needsExtension) {
        console.log('🔄 [Agenda] Navigation en dehors cache, extension nécessaire');
        setRefreshTrigger(prev => prev + 1);
      }
    };
    
    checkAndExtendCache();
  }, [startOfWeek, endOfWeek]);
  
  // Refresh intelligent au focus - seulement si cache périmé
  useEffect(() => {
    const handleFocus = () => {
      const meta = cacheGet('ops/overrides:meta');
      const isStale = !meta || (Date.now() - meta.lastUpdate) > (30 * 60 * 1000); // 30min
      if (isStale) {
        console.log('🔄 [Agenda] Refresh au focus - cache périmé');
        setRefreshTrigger(prev => prev + 1);
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);
  
  // Fallback : vérification périodique si SSE ne fonctionne pas
  useEffect(() => {
    const checkForUpdates = () => {
      const meta = cacheGet('ops/overrides:meta');
      const lastSSEActivity = localStorage.getItem('lastSSEActivity');
      const now = Date.now();
      
      // Si pas d'activité SSE depuis 2 minutes, forcer un refresh
      if (!lastSSEActivity || (now - Number(lastSSEActivity)) > (2 * 60 * 1000)) {
        console.log('⚠️ [Agenda] Pas d\'activité SSE récente, refresh de sécurité');
        setRefreshTrigger(prev => prev + 1);
        localStorage.setItem('lastSSEActivity', String(now));
      }
    };
    
    const interval = setInterval(checkForUpdates, 2 * 60 * 1000); // 2 minutes
    return () => clearInterval(interval);
  }, []);
  
  // Polling supprimé - SSE gère le temps réel
  // Plus de refresh périodique inutile
  
  // 🎓 Charger les configurations école (school_schedules)
  const { schools } = useSchoolSchedules();

  // Debug: voir ce qu'on a dans le cache
  useEffect(() => {
    console.log('📦 Cache opsList:', opsList);
    console.log('📦 Cache weeklyHours:', weeklyHours);
    console.log('📦 Type weeklyHours:', typeof weeklyHours, Array.isArray(weeklyHours) ? 'ARRAY' : 'OBJECT');
  }, [opsList, weeklyHours]);

  // Opérateurs actifs (filtre par status='active')
  const operators = useMemo(() => {
    const ops = (Array.isArray(opsList) ? opsList : [])
      .filter(op => op.status === 'active')
      .map(op => ({
        id: op.id,
        name: op.first_name && op.last_name ? `${op.first_name} ${op.last_name}` : `Op ${op.id}`,
        color: op.color || '#5fa8ff'
      }));
    console.log('👥 Opérateurs actifs:', ops);
    return ops;
  }, [opsList]);

  // Jours d'ouverture (days où au moins un opérateur a des heures configurées)
  // weeklyHours format: { [opId]: { "1": {...}|null, ..., "7": {...}|null } }
  const openDays = useMemo(() => {
    const set = new Set(); // 0..6 pour Lun..Dim
    if (weeklyHours && typeof weeklyHours === 'object') {
      Object.values(weeklyHours).forEach(week => {
        if (!week) return;
        for (let wd = 1; wd <= 7; wd++) {
          if (week[String(wd)]) {
            set.add(wd - 1); // lundi=0
          }
        }
      });
    }
    return set;
  }, [weeklyHours]);

  // Plage de dates à afficher
  const [rangeStart, rangeEnd] = useMemo(() => {
    if (view === 'week') {
      return [startOfWeek(currentDate), endOfWeek(currentDate)];
    }
    return [startOfMonth(currentDate), endOfMonth(currentDate)];
  }, [view, currentDate]);

  // Générer les jours à afficher
  const days = useMemo(() => {
    const arr = [];
    const current = new Date(rangeStart);
    while (current <= rangeEnd) {
      arr.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return arr;
  }, [rangeStart, rangeEnd]);

  // Filtrer selon "jours d'ouverture"
  const visibleDays = useMemo(() => {
    if (!showOpenDaysOnly) return days;
    return days.filter(d => {
      const dow = d.getDay();
      const mondayIdx = dow === 0 ? 6 : dow - 1;
      return openDays.has(mondayIdx);
    });
  }, [days, showOpenDaysOnly, openDays]);

  // Opérateurs filtrés
  const visibleOperators = useMemo(() => {
    if (selectedOperators.length === 0) return operators;
    const filtered = operators.filter(op => selectedOperators.includes(op.id));
    return filtered;
  }, [operators, selectedOperators]);

  // 🔄 LOADER AUTOMATIQUE D'OVERRIDES pour la période visible
  useEffect(() => {
    const from = ymd(rangeStart);
    const to = ymd(rangeEnd);

    // Plus de nettoyage brutal - utilisation intelligente du cache
    
    // Vérifier si on a des opérateurs visibles (sans les inclure dans les dépendances)
    const currentVisibleOps = operators.filter(op => op.status === 'active');
    if (!currentVisibleOps || currentVisibleOps.length === 0) {
      return;
    }

    // 1) Lire le cache actuel - SANS le vider
    let overridesObj = cacheGet('ops/overrides') || {};

    // 2) Si ARRAY, convertir en OBJECT groupé par operator_id
    if (Array.isArray(overridesObj)) {
      const grouped = {};
      for (const it of overridesObj) {
        const oid = Number(it?.operator_id);
        if (!oid) continue;
        if (!grouped[oid]) grouped[oid] = [];
        grouped[oid].push({
          date: String(it.date).split('T')[0],
          is_off: !!it.is_off,
          start_local: it.start_local ?? null,
          end_local: it.end_local ?? null,
          end_next_day: !!it.end_next_day,
          tz: it.tz ?? 'Europe/Paris',
          note: it.note ?? null,
        });
      }
      overridesObj = grouped;
      cacheSet('ops/overrides', overridesObj);
      emitCacheUpdated('ops/overrides');
    }

    // 3) Vérifier si on a déjà les données pour cette période en cache
    const cacheMetadata = cacheGet('ops/overrides:meta') || { from: null, to: null, lastUpdate: 0 };
    const MAX_AGE_MS = 30 * 60 * 1000; // 30 min - SSE gère les mises à jour temps réel
    
    // On fetch si : pas de metadata, hors période, cache périmé, OU refresh forcé (toutes les 30s)
    let needFetch = false;
    let reason = '';
    
    if (refreshTrigger > 0 && (Date.now() - cacheMetadata.lastUpdate) > MAX_AGE_MS) {
      needFetch = true;
      reason = 'Refresh demandé et cache périmé';
    } else if (!cacheMetadata.from || !cacheMetadata.to) {
      needFetch = true;
      reason = 'Pas de metadata';
    } else if (from < cacheMetadata.from || to > cacheMetadata.to) {
      needFetch = true;
      reason = `Extension de période (${cacheMetadata.from} → ${cacheMetadata.to})`;
    } else if (!cacheMetadata.lastUpdate || (Date.now() - cacheMetadata.lastUpdate) > MAX_AGE_MS) {
      needFetch = true;
      reason = `Cache périmé (> ${Math.round(MAX_AGE_MS/60000)} min)`;
    } else {
    }

    if (!needFetch) return;

    
    // 🔥 Activer l'animation de chargement
    setLoadingOverrides(true);

    let aborted = false;
    (async () => {
      try {
        // 🔥 UNE SEULE requête bulk pour tous les opérateurs du user
        const res = await fetch(API_ENDPOINTS.OP_OVERRIDES_BULK(from, to), {
          headers: getAuthHeaders(),
        });
        
        if (!res.ok) {
          console.warn('⚠️ [Agenda] Bulk overrides fetch failed:', res.status);
          setLoadingOverrides(false);
          return;
        }

        const data = await res.json(); // OBJECT { [opId]: Override[] }

        if (aborted || !data || typeof data !== 'object') {
          setLoadingOverrides(false);
          return;
        }


        // Normaliser les dates
        const normalized = {};
        for (const [k, arr] of Object.entries(data)) {
          const oid = Number(k);
          if (!oid) continue;
          
          normalized[oid] = (Array.isArray(arr) ? arr : []).map(o => ({
            date: String(o.date).includes('T') ? String(o.date).split('T')[0] : String(o.date),
            is_off: !!o.is_off,
            start_local: o.start_local ?? null,
            end_local: o.end_local ?? null,
            end_next_day: !!o.end_next_day,
            tz: o.tz ?? 'Europe/Paris',
            note: o.note ?? null,
          }));
        }

        // Merger avec cache existant (dedupe par date)
        const merged = { ...(overridesObj || {}) };
        for (const [oidStr, arr] of Object.entries(normalized)) {
          const oid = Number(oidStr);
          const existing = Array.isArray(merged[oid]) ? merged[oid] : [];
          const byDate = new Map(existing.map(o => [String(o.date), o]));
          
          for (const o of arr) {
            byDate.set(String(o.date), o);
          }
          
          merged[oid] = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
        }

        // Mise à jour du cache - source unique de vérité
        cacheSet('ops/overrides', merged);
        emitCacheUpdated('ops/overrides');
        
        // Mise à jour des métadonnées de période
        const currentMeta = cacheGet('ops/overrides:meta') || { from, to };
        const newMeta = {
          from: !currentMeta.from || from < currentMeta.from ? from : currentMeta.from,
          to: !currentMeta.to || to > currentMeta.to ? to : currentMeta.to,
          lastUpdate: Date.now()
        };
        cacheSet('ops/overrides:meta', newMeta);
        
        
        // 🔥 Désactiver l'animation
        setLoadingOverrides(false);
        
      } catch (e) {
        console.error('❌ [Agenda] Failed to bulk load overrides:', e);
        setLoadingOverrides(false);
      }
    })();

    return () => { 
      aborted = true;
      setLoadingOverrides(false);
    };
  }, [view, currentDate, rangeStart, rangeEnd, refreshTrigger]);

  // Transforme les overrides du cache en map pour accès rapide
  const overridesData = useMemo(() => {
    // Cache comme source unique de vérité
    const sourceData = overridesCache;
    
    console.log('📦 Source overrides: CACHE');
    console.log('📦 Overrides brut:', sourceData);
    console.log('📦 Type:', typeof sourceData, Array.isArray(sourceData) ? 'ARRAY' : 'OBJECT');
    
    const map = {};
    
    // 🔥 VÉRIFICATION: Si c'est null ou undefined, retourner map vide
    if (!sourceData) {
      console.warn('⚠️ sourceData est null/undefined');
      return map;
    }
    
    if (typeof sourceData !== 'object') {
      console.warn('⚠️ sourceData pas un objet, type:', typeof sourceData);
      return map;
    }
    
    // 🔥 FIX: Gérer les 2 formats possibles (ARRAY ou OBJECT)
    if (Array.isArray(sourceData)) {
      // Format: Array d'overrides [{operator_id: 17, date: "2025-10-03", ...}, ...]
      console.log('  📝 Format ARRAY détecté, length:', sourceData.length);
      
      sourceData.forEach((override, idx) => {
        console.log(`    [${idx}] Override:`, JSON.stringify(override));
        
        if (!override || typeof override !== 'object') {
          console.warn(`    ⚠️ Override ${idx} invalide:`, override);
          return;
        }
        
        const opId = override.operator_id;
        let date = override.date;
        
        if (!opId || !date) {
          console.warn(`    ⚠️ Override ${idx} sans operator_id ou date:`, override);
          return;
        }
        
        // Normaliser la date
        if (typeof date === 'string' && date.includes('T')) {
          date = date.split('T')[0];
        }
        
        const key = `${opId}-${date}`;
        console.log(`    ✅ Ajout clé: "${key}" → is_off=${override.is_off}, start=${override.start_local}`);
        map[key] = override;
      });
    } else {
      // Format: Object { "17": [{date:"2025-10-03", ...}], "23": [...] }
      console.log('  📝 Format OBJECT détecté');
      
      Object.entries(sourceData).forEach(([opId, overridesArray]) => {
        console.log('  📝 Processing opId:', opId);
        
        if (!Array.isArray(overridesArray)) {
          console.warn('  ⚠️ overridesArray pas un array pour opId', opId);
          return;
        }
        
        overridesArray.forEach((override, idx) => {
          if (!override || typeof override !== 'object') {
            console.warn(`    ⚠️ Override ${idx} invalide:`, override);
            return;
          }
          
          let date = override.date;
          if (!date) {
            console.warn(`    ⚠️ Override ${idx} sans date:`, override);
            return;
          }
          
          // Normaliser la date
          if (typeof date === 'string' && date.includes('T')) {
            date = date.split('T')[0];
          }
          
          const key = `${opId}-${date}`;
          console.log(`    ✅ Ajout clé: "${key}" → is_off=${override.is_off}`);
          map[key] = override;
        });
      });
    }
    
    console.log('💾 OverridesData map FINAL:', JSON.stringify(map, null, 2));
    console.log('💾 Clés disponibles:', Object.keys(map));
    console.log('💾 Nombre total:', Object.keys(map).length);
    return map;
  }, [overridesCache]);

  // Calcule les horaires d'un opérateur pour un jour donné
  const getShiftForDay = (operatorId, date) => {
    const dateStr = ymd(date);
    const key = `${operatorId}-${dateStr}`;
    
    // 1. Chercher un override pour ce jour
    const override = overridesData[key];
    
    if (override) {
      if (override.is_off) {
        return { type: 'off', label: 'Fermé' };
      }
      const start = (override.start_local || '').slice(0, 5);
      const end = (override.end_local || '').slice(0, 5);
      return {
        type: 'exception',
        start,
        end,
        label: 'Exceptionnel'
      };
    }
    
    // 2. 🎓 Vérifier si c'est un jour de formation (PRIORITAIRE sur template hebdo)
    const schoolShift = getSchoolForDay(schools, operatorId, date);
    if (schoolShift) {
      return schoolShift;
    }
    
    // 3. Chercher le template hebdo
    const dayOfWeek = date.getDay();
    const weekday = dayOfWeek === 0 ? 7 : dayOfWeek;
    
    const template = weeklyHours?.[operatorId]?.[String(weekday)];
    
    if (template) {
      const start = (template.start || template.start_local || '').slice(0, 5);
      const end = (template.end || template.end_local || '').slice(0, 5);
      
      return {
        type: 'recurring',
        start,
        end,
        label: 'Récurrent'
      };
    }
    
    // 4. Pas d'horaires configurés
    return null;
  };

  // Ouvre le popup d'édition
  const handleEditCell = (opId, date) => {
    const shift = getShiftForDay(opId, date);
    setEditPopup({ opId, date, shift });
  };

  // Sauvegarde depuis le popup
  const handleSaveFromPopup = async (formData) => {
    const { opId, date, startTime, endTime, isAbsent, isPermanent } = formData;
    const dateStr = ymd(date);
    
    console.log('💾 [handleSaveFromPopup] Données reçues:', { 
      opId, 
      dateStr, 
      startTime, 
      endTime, 
      isAbsent, 
      isPermanent 
    });
    
    try {
      if (isPermanent) {
        // Modification DEFINITIVE du template hebdo
        const dayOfWeek = date.getDay();
        const weekday = dayOfWeek === 0 ? 7 : dayOfWeek;
        
        if (isAbsent) {
          // Supprimer ce jour du template
          await fetch(API_ENDPOINTS.OP_WEEKLY_HOURS_DELETE_DAY(opId, weekday), {
            method: 'DELETE',
            headers: getAuthHeaders()
          });
          
          // Update cache local
          const currentCache = cacheGet('ops/weeklyHours') || {};
          if (currentCache[opId]) {
            currentCache[opId][String(weekday)] = null;
            cacheSet('ops/weeklyHours', currentCache);
            emitCacheUpdated('ops/weeklyHours');
          }
        } else {
          // Modifier le template pour ce weekday
          await fetch(API_ENDPOINTS.OP_WEEKLY_HOURS_POST(opId), {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              weekday,
              start_local: startTime,
              end_local: endTime,
              end_next_day: false,
              tz: 'Europe/Paris',
              effective_from: dateStr
            })
          });
          
          // Update cache local
          const currentCache = cacheGet('ops/weeklyHours') || {};
          if (!currentCache[opId]) currentCache[opId] = {};
          currentCache[opId][String(weekday)] = {
            start: startTime,
            end: endTime,
            end_next_day: false
          };
          cacheSet('ops/weeklyHours', currentCache);
          emitCacheUpdated('ops/weeklyHours');
        }
        
      } else {
        // Modification EXCEPTIONNELLE (override ponctuel)
        let response;
        
        if (isAbsent) {
          response = await fetch(API_ENDPOINTS.OP_OVERRIDE_UPSERT(opId, dateStr), {
            method: 'PUT',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              is_off: true,
              note: 'Absence exceptionnelle via Agenda'
            })
          });
        } else {
          response = await fetch(API_ENDPOINTS.OP_OVERRIDE_UPSERT(opId, dateStr), {
            method: 'PUT',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              is_off: false,
              start_local: startTime,
              end_local: endTime,
              end_next_day: false,
              tz: 'Europe/Paris',
              note: 'Horaires exceptionnels via Agenda'
            })
          });
        }
        
        // 🔥 VÉRIFIER si l'API a réussi
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ [Agenda] Erreur API:', response.status, errorText);
          throw new Error(`Erreur ${response.status}: ${errorText}`);
        }
        
        const result = await response.json();

        // Mise à jour du cache directement
        let currentOverrides = cacheGet('ops/overrides') || {};
        
        // 🔥 IMPORTANT: Convertir en OBJECT si c'est un ARRAY (harmonisation format)
        if (Array.isArray(currentOverrides)) {
          const converted = {};
          currentOverrides.forEach(override => {
            const oid = override.operator_id;
            if (!oid) return;
            if (!converted[oid]) converted[oid] = [];
            converted[oid].push(override);
          });
          currentOverrides = converted;
        }
        
        // S'assurer que l'array existe pour cet opId
        if (!currentOverrides[opId]) {
          currentOverrides[opId] = [];
        }
        
        // Créer le nouvel override (SANS operator_id car c'est la clé)
        const newOverride = {
          date: dateStr,
          is_off: isAbsent,
          start_local: isAbsent ? null : startTime,
          end_local: isAbsent ? null : endTime,
          end_next_day: false,
          note: isAbsent ? 'Absence exceptionnelle via Agenda' : 'Horaires exceptionnels via Agenda'
        };
        
        // Trouver si override existe déjà pour cette date
        const existingIdx = currentOverrides[opId].findIndex(o => o.date === dateStr);
        
        if (existingIdx >= 0) {
          // Remplacer
          currentOverrides[opId][existingIdx] = newOverride;
        } else {
          // Ajouter
          currentOverrides[opId].push(newOverride);
        }
        
        
        // Le cache est mis à jour, useCache se charge du re-render automatique
        console.log('✅✅✅ [AGENDA] CACHE MIS À JOUR = AFFICHAGE AUTOMATIQUE');
        
        // Mettre à jour le cache après (pour persistance et sync multi-onglets)
        cacheSet('ops/overrides', currentOverrides);
        emitCacheUpdated('ops/overrides');
        
        // 🚀 ÉVÉNEMENT MANUEL pour garantir la mise à jour immédiate
        console.log('🚀 [Agenda] Émission événement manuel après sauvegarde');
        const manualEvent = {
          type: 'op:override:upsert',
          operator_id: opId,
          date: dateStr,
          is_off: isAbsent,
          start_local: isAbsent ? null : startTime,
          end_local: isAbsent ? null : endTime,
          end_next_day: false,
          tz: 'Europe/Paris',
          note: isAbsent ? 'Absence exceptionnelle via Agenda' : 'Horaires exceptionnels via Agenda'
        };
        
        // Importer et appeler agendaSelectiveRefresh directement
        import('../../api/bootstrap.js').then(({ agendaSelectiveRefresh }) => {
          agendaSelectiveRefresh(manualEvent);
        }).catch(console.error);
      }

      setEditPopup(null);
      setToast({ message: 'Modification enregistrée', type: 'success' });
      setTimeout(() => setToast(null), 3000);

    } catch (err) {
      console.error('Erreur save:', err);
      setToast({ message: 'Erreur lors de la sauvegarde', type: 'error' });
      setTimeout(() => setToast(null), 3000);
    }
  };

  // Render cellule
  const renderCell = (opId, date) => {
    const shift = getShiftForDay(opId, date);

    // Cas 1 : Absence exceptionnelle
    if (shift?.type === 'off') {
      return (
        <div className="agenda-cell cell-off" onClick={() => handleEditCell(opId, date)}>
          <div className="shift-tag shift-off">Fermé</div>
        </div>
      );
    }

    // Cas 2 : Pas d'horaires
    if (!shift) {
      return (
        <div className="agenda-cell" onClick={() => handleEditCell(opId, date)}>
          <div className="cell-empty">—</div>
        </div>
      );
    }
    
    // Cas 3 : 🎓 Formation (ROSE - cliquable pour exceptions)
    if (shift.type === 'school') {
      return (
        <div className="agenda-cell cell-school" onClick={() => handleEditCell(opId, date)}>
          <div className="shift-tag shift-school" title="Formation - Cliquez pour modifier exceptionnellement">
            {shift.start}–{shift.end}
          </div>
        </div>
      );
    }

    // Cas 4 : Ouverture exceptionnelle (vert)
    if (shift.type === 'exception') {
      return (
        <div className="agenda-cell cell-exception" onClick={() => handleEditCell(opId, date)}>
          <div className="shift-tag shift-exception" title="Ouverture exceptionnelle">
            {shift.start}–{shift.end}
          </div>
        </div>
      );
    }

    // Cas 5 : Ouverture récurrente (bleu normal)
    return (
      <div className="agenda-cell" onClick={() => handleEditCell(opId, date)}>
        <div className="shift-tag shift-recurring">
          {shift.start}–{shift.end}
        </div>
      </div>
    );
  };

  // Navigation
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Toggle opérateur
  const toggleOperator = (opId) => {
    setSelectedOperators(prev => {
      if (prev.includes(opId)) {
        return prev.filter(id => id !== opId);
      }
      return [...prev, opId];
    });
  };

  // Titre de la période
  const periodTitle = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      const startStr = `${start.getDate()} ${MONTH_NAMES_FR[start.getMonth()]}`;
      const endStr = `${end.getDate()} ${MONTH_NAMES_FR[end.getMonth()]} ${end.getFullYear()}`;
      return `${startStr} – ${endStr}`;
    }
    return `${MONTH_NAMES_FR[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  }, [view, currentDate]);

  return (
    <div className="agenda-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo">CabConnect</div>
        <nav className="nav-menu">
          <div className="nav-item" onClick={() => onNavigate && onNavigate('tasks')}>
            <span className="nav-icon">📁</span>
            <span>Tâches</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('sterilization')}>
            <span className="nav-icon">📦</span>
            <span>Stérilisation</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('operators')}>
            <span className="nav-icon">👤</span>
            <span>Opérateurs</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('activity')}>
            <span className="nav-icon">📊</span>
            <span>Activité</span>
          </div>
          <div className="nav-item active" onClick={() => onNavigate && onNavigate('agenda')}>
            <span className="nav-icon">🗓️</span>
            <span>Agenda</span>
          </div>
          <div className="nav-item" onClick={() => onNavigate && onNavigate('orders')}>
            <span className="nav-icon">🛒</span>
            <span>Commandes</span>
          </div>
        </nav>
      </div>

      {/* Contenu principal */}
      <div className="agenda-content">
        {/* Header */}
        <div className="agenda-header">
          <div className="header-left">
            <h1>Agenda</h1>
            <div className="view-toggle">
              <button
                className={`toggle-btn ${view === 'week' ? 'active' : ''}`}
                onClick={() => setView('week')}
              >
                Semaine
              </button>
              <button
                className={`toggle-btn ${view === 'month' ? 'active' : ''}`}
                onClick={() => setView('month')}
              >
                Mois
              </button>
            </div>
          </div>
          <div className="header-right">
            <button className="nav-btn" onClick={goToPrevious}>←</button>
            <button className="today-btn" onClick={goToToday}>Aujourd'hui</button>
            <button className="nav-btn" onClick={goToNext}>→</button>
          </div>
        </div>

        {/* Période */}
        <div className="period-title">{periodTitle}</div>

        {/* Filtres */}
        <div className="filters-bar">
          <div className="filter-group">
            <label className="filter-label">Opérateurs :</label>
            <div className="op-chips">
              {operators.map(op => (
                <button
                  key={op.id}
                  className={`chip ${selectedOperators.length === 0 || selectedOperators.includes(op.id) ? 'active' : ''}`}
                  onClick={() => toggleOperator(op.id)}
                  style={{
                    borderColor: selectedOperators.includes(op.id) || selectedOperators.length === 0 ? op.color : 'transparent'
                  }}
                >
                  {op.name}
                </button>
              ))}
            </div>
          </div>
          <div className="filter-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showOpenDaysOnly}
                onChange={(e) => setShowOpenDaysOnly(e.target.checked)}
              />
              Jours d'ouverture uniquement
            </label>
          </div>
        </div>

        {/* Grille Agenda */}
        {view === 'week' ? (
          // Vue Semaine (ancien format avec lignes par opérateur)
          <div className={`agenda-grid ${loadingOverrides ? 'agenda-loading' : ''}`}>
            <div className="grid-header">
              <div className="corner-cell"></div>
              {visibleDays.map((day, idx) => {
                const dow = day.getDay();
                const mondayIdx = dow === 0 ? 6 : dow - 1;
                const dayName = DAY_NAMES_FR[mondayIdx];
                const dateNum = day.getDate();
                return (
                  <div key={idx} className="day-header">
                    <div className="day-name">{dayName}</div>
                    <div className="day-num">{dateNum}</div>
                  </div>
                );
              })}
            </div>

            {visibleOperators.map(op => (
              <div key={op.id} className="grid-row">
                <div className="op-label" style={{ borderLeftColor: op.color }}>
                  {op.name}
                </div>
                {visibleDays.map((day, idx) => (
                  <div key={idx}>
                    {renderCell(op.id, day)}
                  </div>
                ))}
              </div>
            ))}

            {visibleOperators.length === 0 && (
              <div className="empty-state">
                Aucun opérateur sélectionné
              </div>
            )}
          </div>
        ) : (
          // Vue Mois (calendrier avec opérateurs dans les cases)
          <div className="month-calendar">
            {/* Header des jours de la semaine */}
            <div className="month-header">
              {DAY_NAMES_FR.map((day, idx) => (
                <div key={idx} className="month-day-name">{day.slice(0, 3)}</div>
              ))}
            </div>

            {/* Grille des jours */}
            <div className="month-grid">
              {visibleDays.map((day, idx) => {
                const dateStr = ymd(day);
                const isToday = dateStr === ymd(new Date());
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                
                // Récupérer tous les opérateurs qui travaillent ce jour
                const operatorsOnDay = visibleOperators
                  .map(op => {
                    const shift = getShiftForDay(op.id, day);
                    return shift ? { ...op, shift } : null;
                  })
                  .filter(Boolean);

                return (
                  <div 
                    key={idx} 
                    className={`month-cell ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                  >
                    <div className="month-cell-date">{day.getDate()}</div>
                    <div className="month-cell-operators">
                      {operatorsOnDay.map(op => {
                        const isSchool = op.shift.type === 'school';
                        return (
                          <div 
                            key={op.id}
                            className={`op-badge op-badge-${op.shift.type}`}
                            style={isSchool ? { 
                              backgroundColor: 'rgba(255, 105, 180, 0.2)',
                              borderColor: '#ff69b4',
                              color: '#ff69b4'
                            } : { 
                              backgroundColor: op.color + '20',
                              borderColor: op.color,
                              color: op.color
                            }}
                            onClick={() => handleEditCell(op.id, day)}
                            title={`${op.name}: ${op.shift.type === 'off' ? 'Fermé' : op.shift.type === 'school' ? 'Formation' : op.shift.start + '–' + op.shift.end}`}
                          >
                            {op.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`toast toast-${toast.type}`}>
            <span className="toast-icon">
              {toast.type === 'success' && '✓'}
              {toast.type === 'error' && '✕'}
            </span>
            <span className="toast-message">{toast.message}</span>
          </div>
        )}

        {/* Popup d'édition */}
        {editPopup && (
          <EditPopup
            operator={operators.find(o => o.id === editPopup.opId)}
            date={editPopup.date}
            shift={editPopup.shift}
            onSave={handleSaveFromPopup}
            onCancel={() => setEditPopup(null)}
          />
        )}
      </div>
    </div>
  );
}

// Composant Popup d'édition
function EditPopup({ operator, date, shift, onSave, onCancel }) {
  const dateStr = ymd(date);
  const dayOfWeek = date.getDay();
  const weekday = dayOfWeek === 0 ? 7 : dayOfWeek;
  const dayNameFull = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'][dayOfWeek];
  
  // 🎓 Détecter si c'est un jour de formation
  const isSchoolDay = shift?.type === 'school';
  
  // Valeurs par défaut
  const [startTime, setStartTime] = useState(shift?.start || '09:00');
  const [endTime, setEndTime] = useState(shift?.end || '19:00');
  const [isAbsent, setIsAbsent] = useState(shift?.type === 'off');
  const [isPermanent, setIsPermanent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // 🎓 VALIDATION CRITIQUE: Bloquer modification permanente sur jour de formation
    if (isPermanent && isSchoolDay) {
      setError('❌ Modification permanente impossible sur un jour de formation. Cet opérateur a un contrat d\'apprentissage en cours. Pour supprimer définitivement ce jour d\'école, veuillez modifier les informations de son contrat dans la section Opérateurs.');
      return;
    }
    
    // Validation
    if (!isAbsent && (!startTime || !endTime)) {
      setError('Veuillez remplir les horaires');
      return;
    }
    
    if (!isAbsent) {
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      if (sh * 60 + sm >= eh * 60 + em) {
        setError('L\'heure de début doit être avant l\'heure de fin');
        return;
      }
    }

    setLoading(true);
    
    try {
      await onSave({
        opId: operator.id,
        date,
        startTime,
        endTime,
        isAbsent,
        isPermanent
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="popup-overlay" onClick={onCancel}>
      <div className="popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <h3>Modifier les horaires</h3>
          <button className="popup-close" onClick={onCancel}>✕</button>
        </div>

        <div className="popup-body">
          <div className="popup-info">
            <div className="info-row">
              <span className="info-label">Opérateur :</span>
              <span className="info-value">{operator?.name}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Date :</span>
              <span className="info-value">{dayNameFull} {date.getDate()} {MONTH_NAMES_FR[date.getMonth()]} {date.getFullYear()}</span>
            </div>
            {shift?.type && (
              <div className="info-row">
                <span className="info-label">Actuellement :</span>
                <span className={`info-badge badge-${shift.type}`}>
                  {shift.type === 'recurring' && '🔄 Récurrent'}
                  {shift.type === 'exception' && '⚡ Exceptionnel'}
                  {shift.type === 'off' && '🚫 Fermé'}
                  {shift.type === 'school' && '🎓 Formation'}
                </span>
              </div>
            )}
            
            {/* Alerte si jour d'école */}
            {isSchoolDay && (
              <div className="info-alert">
                ℹ️ Ce jour est configuré comme jour de formation. Vous pouvez créer une ouverture ou absence <strong>exceptionnelle</strong> uniquement.
              </div>
            )}
          </div>
          
          {/* Message d'erreur */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Toggle Absent */}
            <div className="form-group">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={isAbsent}
                  onChange={(e) => setIsAbsent(e.target.checked)}
                />
                <span className="checkbox-label">🚫 Marquer comme absent ce jour</span>
              </label>
            </div>

            {/* Horaires (désactivés si absent) */}
            {!isAbsent && (
              <div className="form-row">
                <div className="form-group">
                  <label>Heure de début</label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="time-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Heure de fin</label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="time-input"
                    required
                  />
                </div>
              </div>
            )}

            {/* Type de modification */}
            <div className="form-group">
              <label className="section-label">Type de modification</label>
              <div className="radio-group">
                <label className="radio-container">
                  <input
                    type="radio"
                    name="modType"
                    checked={!isPermanent}
                    onChange={() => setIsPermanent(false)}
                  />
                  <div className="radio-content">
                    <span className="radio-title">⚡ Exceptionnel</span>
                    <span className="radio-desc">Uniquement pour ce {dayNameFull} {date.getDate()} {MONTH_NAMES_FR[date.getMonth()]}</span>
                  </div>
                </label>
                <label className="radio-container">
                  <input
                    type="radio"
                    name="modType"
                    checked={isPermanent}
                    onChange={() => setIsPermanent(true)}
                  />
                  <div className="radio-content">
                    <span className="radio-title">🔄 Récurrent</span>
                    <span className="radio-desc">Pour tous les {dayNameFull}s futurs</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="popup-actions">
              <button type="button" className="btn-cancel" onClick={onCancel} disabled={loading}>
                Annuler
              </button>
              <button type="submit" className="btn-save" disabled={loading}>
                <div className="btn-save-content">
                  {loading && <div className="spinner"></div>}
                  <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
                </div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Agenda;

